import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  ArrowRight, 
  MapPin, 
  Clock, 
  Briefcase, 
  Search,
  Filter,
  AlertCircle,
  ChevronRight,
  Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import PageHeader from '../components/layout/PageHeader';
import { jobsApi, applicationsApi } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export default function CareersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedSkill, setSelectedSkill] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [skills, setSkills] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const [applicationForm, setApplicationForm] = useState({
    name: '',
    email: '',
    phone: '',
    linkedin_url: '',
    cover_letter: ''
  });

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await jobsApi.getAll({ active_only: true });
      setJobs(response.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching jobs:', err);
      }
      toast.error('Failed to load job listings');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFilters = useCallback(async () => {
    try {
      const [deptRes, skillsRes] = await Promise.all([
        jobsApi.getDepartments(),
        jobsApi.getSkills()
      ]);
      setDepartments(deptRes.data || []);
      setSkills(skillsRes.data || []);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching filters:', err);
      }
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    fetchFilters();
  }, [fetchJobs, fetchFilters]);

  const filteredJobs = useMemo(() => jobs.filter(job => {
    const matchesSearch = !searchQuery || 
      job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDepartment = selectedDepartment === 'all' || job.department === selectedDepartment;
    const matchesSkill = selectedSkill === 'all' || job.skills.includes(selectedSkill);
    return matchesSearch && matchesDepartment && matchesSkill;
  }), [jobs, searchQuery, selectedDepartment, selectedSkill]);

  const handleApply = async (e) => {
    e.preventDefault();
    if (!selectedJob) return;

    setApplying(true);
    try {
      await applicationsApi.submit({
        job_id: selectedJob.id,
        ...applicationForm
      });
      toast.success('Application submitted successfully! We will review it shortly.');
      setApplyDialogOpen(false);
      setApplicationForm({ name: '', email: '', phone: '', linkedin_url: '', cover_letter: '' });
      setSelectedJob(null);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting application:', err);
      }
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="pt-20">
      <PageHeader 
        title="Careers at GITA"
        subtitle="Join a team that's shaping the future of IT staffing and technology consulting. Explore opportunities that match your skills."
        breadcrumbs={[{ label: "Careers" }]}
      />

      {/* Search & Filters */}
      <section className="py-8 bg-white border-b border-slate-100 sticky top-20 z-30" data-testid="filters-section">
        <div className="container-padding">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input 
                placeholder="Search jobs by title or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-slate-50 border-slate-200"
                data-testid="job-search-input"
              />
            </div>
            
            <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
              <SelectTrigger className="w-full lg:w-48 h-12" data-testid="department-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedSkill} onValueChange={setSelectedSkill}>
              <SelectTrigger className="w-full lg:w-48 h-12" data-testid="skill-filter">
                <SelectValue placeholder="Skill" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Skills</SelectItem>
                {skills.map((skill) => (
                  <SelectItem key={skill} value={skill}>{skill}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="section-padding bg-[#F8FAFC]" data-testid="jobs-section">
        <div className="container-padding">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-600">Loading opportunities...</p>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
              <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No jobs found</h3>
              <p className="text-slate-500 mb-6">Try adjusting your search or filters</p>
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchQuery('');
                  setSelectedDepartment('all');
                  setSelectedSkill('all');
                }}
                data-testid="clear-filters-btn"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <p className="text-slate-600">
                  Showing <span className="font-semibold text-[#0A2540]">{filteredJobs.length}</span> open positions
                </p>
              </div>

              <div className="space-y-4">
                {filteredJobs.map((job, index) => (
                  <div 
                    key={job.id}
                    className="job-card"
                    data-testid={`job-card-${index}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold text-[#0A2540]">
                            {job.title}
                          </h3>
                          {job.is_urgent && (
                            <Badge variant="destructive" className="job-urgent" data-testid="urgent-badge">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Urgent
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-3">
                          <span className="flex items-center">
                            <Briefcase className="w-4 h-4 mr-1" />
                            {job.department}
                          </span>
                          <span className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {job.location}
                          </span>
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {job.employment_type}
                          </span>
                        </div>

                        <p className="text-slate-600 mb-4 line-clamp-2">
                          {job.description}
                        </p>

                        <div className="flex flex-wrap gap-2">
                          {job.skills.slice(0, 5).map((skill) => (
                            <span key={skill} className="job-tag">
                              {skill}
                            </span>
                          ))}
                          {job.skills.length > 5 && (
                            <span className="text-xs text-slate-500">+{job.skills.length - 5} more</span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[160px]">
                        <Dialog open={applyDialogOpen && selectedJob?.id === job.id} onOpenChange={(open) => {
                          setApplyDialogOpen(open);
                          if (open) setSelectedJob(job);
                        }}>
                          <DialogTrigger asChild>
                            <Button 
                              className="btn-primary w-full"
                              data-testid={`apply-btn-${index}`}
                            >
                              Apply Now
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-[500px]">
                            <DialogHeader>
                              <DialogTitle>Apply for {job.title}</DialogTitle>
                              <DialogDescription>
                                Fill in your details to submit your application
                              </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleApply} className="space-y-4 mt-4">
                              <div className="space-y-2">
                                <Label htmlFor="name">Full Name *</Label>
                                <Input 
                                  id="name"
                                  value={applicationForm.name}
                                  onChange={(e) => setApplicationForm({...applicationForm, name: e.target.value})}
                                  required
                                  data-testid="apply-name-input"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="email">Email *</Label>
                                <Input 
                                  id="email"
                                  type="email"
                                  value={applicationForm.email}
                                  onChange={(e) => setApplicationForm({...applicationForm, email: e.target.value})}
                                  required
                                  data-testid="apply-email-input"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="phone">Phone *</Label>
                                <Input 
                                  id="phone"
                                  type="tel"
                                  value={applicationForm.phone}
                                  onChange={(e) => setApplicationForm({...applicationForm, phone: e.target.value})}
                                  required
                                  data-testid="apply-phone-input"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="linkedin">LinkedIn Profile</Label>
                                <Input 
                                  id="linkedin"
                                  value={applicationForm.linkedin_url}
                                  onChange={(e) => setApplicationForm({...applicationForm, linkedin_url: e.target.value})}
                                  placeholder="https://linkedin.com/in/..."
                                  data-testid="apply-linkedin-input"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="cover_letter">Cover Letter</Label>
                                <Textarea 
                                  id="cover_letter"
                                  value={applicationForm.cover_letter}
                                  onChange={(e) => setApplicationForm({...applicationForm, cover_letter: e.target.value})}
                                  placeholder="Tell us why you're a great fit..."
                                  rows={4}
                                  data-testid="apply-cover-letter-input"
                                />
                              </div>
                              <Button 
                                type="submit" 
                                className="w-full btn-primary"
                                disabled={applying}
                                data-testid="submit-application-btn"
                              >
                                {applying ? 'Submitting...' : 'Submit Application'}
                              </Button>
                            </form>
                          </DialogContent>
                        </Dialog>
                        
                        <Link to={`/careers/${job.id}`}>
                          <Button 
                            variant="outline" 
                            className="w-full"
                            data-testid={`view-details-btn-${index}`}
                          >
                            View Details
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Why Join Us */}
      <section className="section-padding bg-white" data-testid="why-join-section">
        <div className="container-padding">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-4 py-1 bg-blue-100 text-[#2563EB] rounded-full text-sm font-medium mb-4">
              Why Join GITA
            </div>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#0A2540]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Build Your Career With Us
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: "Growth Opportunities", description: "Continuous learning and career advancement paths" },
              { title: "Innovative Projects", description: "Work with Fortune 500 clients on cutting-edge tech" },
              { title: "Work-Life Balance", description: "Flexible arrangements and competitive benefits" }
            ].map((benefit, index) => (
              <div 
                key={benefit.title}
                className="text-center p-8 bg-slate-50 rounded-2xl"
                data-testid={`benefit-${index}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-[#2563EB]/10 flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-[#2563EB]" />
                </div>
                <h3 className="text-xl font-semibold text-[#0A2540] mb-3">{benefit.title}</h3>
                <p className="text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A2540]" data-testid="careers-cta">
        <div className="container-padding text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Don't See the Right Role?
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            We're always looking for talented individuals. Send us your resume and we'll reach out when a matching opportunity arises.
          </p>
          <Link to="/contact?type=career_inquiry">
            <Button className="btn-accent text-base" data-testid="submit-resume-btn">
              Submit Your Resume
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
